// CustomNode.js
import { Box, styled, Tooltip, tooltipClasses, Typography } from "@mui/material";
import {
  Handle,
  Position,
  useConnection,
  useReactFlow,
  useStoreApi,
} from "@xyflow/react";
import React, { memo } from "react";
import { AreaCard } from "../area-card";

import { usePlannerContext } from "../../../services/context/planner-context";

// svg
import { ReactComponent as IconRoad } from "../../../assets/svg/road1.svg";
import { ReactComponent as IconStreet } from "../../../assets/svg/street.svg";
import { ReactComponent as IconPedestrian } from "../../../assets/svg/pedestrian.svg";
import { ReactComponent as IconTruck } from "../../../assets/svg/truck.svg";
import { ReactComponent as IconAdjacent } from "../../../assets/svg/adjacent.svg";
import { ADJACENT_TYPE, CAR_PARKING_AREA_TYPE, OFFICE_TYPE, TRUCK_ROUTE_TYPE } from "../../../constants/global.constants";
import { TEXT } from "../../../constants/text";
import { useUserContext } from "../../../services/context/user-context";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.background.default,
    fontSize: 12,
    /*   boxShadow: theme.shadows[1],
     fontSize: 11 */
  },
  /* cursor: 'pointer', */
}));

const handleStyle = {
  width: "38px",
  height: "38px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderWidth: "2px",
};

function IconDisplay({
  type,
  color = "#fff",
  width = "24px",
  height = "24px",
}) {
  switch (type) {
    case "road":
      return (
        <IconRoad
          width={width}
          height={height}
          fill={color}
          style={{ pointerEvents: "none" }}
        />
      );
    case "adjacent":
      return (
        <IconAdjacent
          width={width}
          height={height}
          fill={color}
          style={{ pointerEvents: "none" }}
        />
      );
    case "pedestrian":
      return (
        <IconPedestrian
          width={width}
          height={height}
          fill={color}
          style={{ pointerEvents: "none" }}
        />
      );
    case "truckroute":
      return (
        <IconTruck
          width={width}
          height={height}
          fill={color}
          style={{ pointerEvents: "none" }}
        />
      );
    default:
      return null;
  }
}

const icon = {
  road: (
    <IconRoad
      width={"24px"}
      height={"24px"}
      fill="#fff"
      style={{ pointerEvents: "none" }}
    />
  ),
  adjacent: (
    <IconAdjacent
      width={"24px"}
      height={"24px"}
      fill="#fff"
      style={{ pointerEvents: "none" }}
    />
  ),
  pedestrian: (
    <IconPedestrian
      width={"24px"}
      height={"24px"}
      fill="#fff"
      style={{ pointerEvents: "none" }}
    />
  ),
  truckroute: (
    <IconTruck
      width={"24px"}
      height={"24px"}
      fill="#fff"
      style={{ pointerEvents: "none" }}
    />
  ),
};

const CustomNode = memo(({ id, data, selected }) => {
  const { userData, viewOnly } = useUserContext();
  const connection = useConnection();

  const { edges, connectionTypes } = usePlannerContext();

  const { connectionClickStartHandle } = data;

  const connectionInProgress = connection.inProgress;

  const isTargetNode =
    (connection.inProgress && connection.fromNode.id !== id) ||
    (connectionClickStartHandle && connectionClickStartHandle.nodeId !== id);

  const sourceType =
    (connection.fromHandle && connection.fromHandle.id.split("-")[0]) ||
    (connectionClickStartHandle && connectionClickStartHandle.id.split("-")[0]);

  const alreadyExists =
    edges &&
    !!edges.some((edge) => {
      const isSameType = edge.data?.connectionType === sourceType;

      const connectsTheseTwo = connection.inProgress
        ? edge.source === connection.fromHandle?.nodeId ||
          edge.target === connection.fromHandle?.nodeId
        : connectionClickStartHandle
        ? edge.source === connectionClickStartHandle.nodeId ||
          edge.target === connectionClickStartHandle.nodeId
        : false;

      const currentNode = edge.source === id || edge.target === id;

      return isSameType && connectsTheseTwo && currentNode;
    });


  return (
    <div
      key={id + "node"}
      style={{
        padding: 10,
        minWidth: 150,
        position: "relative",
      }}
    >
      <AreaCard key={id} nodeId={id} nodeData={data} />

      {/* Connection handles with dropdown */}
      {/* <Handle
        type="source"
        position={Position.Right}
        id="target-right"
        style={{ ...handleStyle, right: -8, background: data.color }}
      >
        <span style={{ fontSize: '20px', lineHeight: 1, color: "#fff", marginBottom: '4px', pointerEvents: 'none', }}>
          +
        </span>
      </Handle>
      <Handle
        type="target"
        position="left"
        id="target-left"
        style={{ ...handleStyle, left: -8, background: data.color }}
        >
        <span style={{ fontSize: '20px', lineHeight: 1, color: "#fff", marginBottom: '4px', pointerEvents: 'none', }}>
          +
        </span>
      </Handle> */}

      {/* Connection handles for each type */}
      {connectionTypes &&
        connectionTypes.map((typeObj, index) => {
          const isHandleTarget = viewOnly ? false :
            isTargetNode && sourceType === typeObj.edge_name && !alreadyExists;
          const isHandleSelected = viewOnly ? false :
            !isTargetNode && sourceType === typeObj.edge_name;
          /* connectionClickStartHandle &&
            connectionClickStartHandle.nodeId === id &&
            connectionClickStartHandle.id === typeObj.edge_name + "-right"; */
          if (!userData.isAdmin && (typeObj.edge_name !== ADJACENT_TYPE && typeObj.edge_name !== TRUCK_ROUTE_TYPE)) {
            return null;
          }
          if((data.type === CAR_PARKING_AREA_TYPE || data.type === OFFICE_TYPE) && typeObj.edge_name === TRUCK_ROUTE_TYPE){
            return null;
          }
          return (
            <>
              <LightTooltip
                key={typeObj.edge_name + "-right-tooltip"}
                title={typeObj.display_name}
                placement="right"
              >
                <Handle                
                  key={typeObj.edge_name + "-source-right"}
                  id={typeObj.edge_name + "-right"} // important: used to identify which type
                  type="source"
                  position={Position.Right}
                  style={{
                    ...handleStyle,
                    /* marginTop: -30 + index * 20 + "%", */
                    top: userData.isAdmin ? 20 + index * 20 + "%" : `calc(50% + ${typeObj.edge_name === ADJACENT_TYPE ? -40 : 40}px)`,
                    background: isHandleTarget ? "#efa21e" : typeObj.color,
                    boxShadow: isHandleSelected
                      ? "0 0 12px 2px rgba(239, 162, 30, 0.75)"
                      : "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
                    transition: "box-shadow 0.2s, border 0.2s",
                    right: -18,
                  }}
                >
                  <IconDisplay type={typeObj.edge_name} />
                </Handle>
              </LightTooltip>

              <LightTooltip
                key={typeObj.edge_name + "-left-tooltip"}
                title={typeObj.display_name}
                placement="left"
              >
                <Handle
                  key={typeObj.edge_name + "-source-left"}
                  id={typeObj.edge_name + "-left"} // important: used to identify which type
                  type="source"
                  position={Position.Left}
                  style={{
                    ...handleStyle,
                    top: userData.isAdmin ? 20 + index * 20 + "%" : `calc(50% + ${typeObj.edge_name === ADJACENT_TYPE ? -40 : 40}px)`,
                    background: isHandleTarget ? "#efa21e" : typeObj.color,
                    boxShadow: isHandleSelected
                      ? "0 0 12px 2px rgba(239, 162, 30, 0.75)"
                      : "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
                    transition: "box-shadow 0.2s, border 0.2s",
                    left: -18,
                  }}
                >
                  <IconDisplay type={typeObj.edge_name} />
                </Handle>
              </LightTooltip>
            </>
          );
        })}
    </div>
  );
});

export default CustomNode;
